import { useCallback } from 'react'
import { usePackageInfo } from '@/utils'
import { sendGTMEvent } from './analytics.gtm'
import {
  AnalyticsAddToCartEventProps,
  AnalyticsFormSubmitEventProps,
  AnalyticsPayNowProps,
  AnalyticsSelectItemEventProps,
  AnalyticsViewItemListEventProps,
} from './analytics.types'
import {
  ANALYTICS_EVENTS,
  CART_TYPE,
  CartType,
  EVENT_VARIANTS,
  EventVariantKeys,
} from './analytics.data'
import { getCartProducts } from './useAnalytics.utils'
import { EXPERIENCE_ANALYTICS, useUserExperiences } from '../userExperience'

const useAnalytics = () => {
  const { currentPackage } = usePackageInfo()
  const { isReviewEnabled } = useUserExperiences()
  const bookingVariant = isReviewEnabled
    ? EXPERIENCE_ANALYTICS.EXP_REVIEW_PAGE_DEFAULT
    : EXPERIENCE_ANALYTICS.EXP_REVIEW_PAGE_HIDDEN

  const sendAnalytics = useCallback(
    (eventName: string, eventData: object) => sendGTMEvent({ event: `${eventName}`, ...eventData }),
    [],
  )

  const sendNavigationEvent = useCallback(
    (
      navText: string,
      navButton: string,
      navLocation: string = ANALYTICS_EVENTS.NAVIGATION_LOCATION.HEADER,
    ) =>
      sendAnalytics(ANALYTICS_EVENTS.EVENT_TYPES.NAVIGATION, {
        nav_button: navButton,
        nav_location: navLocation,
        nav_text: navText,
      }),
    [sendAnalytics],
  )

  const sendFormSubmitEvent = useCallback(
    (
      formData: AnalyticsFormSubmitEventProps,
      eventName: string = ANALYTICS_EVENTS.EVENT_TYPES.FORM_SUBMIT,
    ) => sendAnalytics(eventName, formData),
    [sendAnalytics],
  )

  const clearEcommerceEvent = useCallback(() => sendGTMEvent({ ecommerce: null }), [])

  const sendViewItemListEvent = useCallback(
    ({ id, name, variant, items }: AnalyticsViewItemListEventProps) => {
      clearEcommerceEvent()
      sendAnalytics(ANALYTICS_EVENTS.EVENT_TYPES.VIEW_ITEM_LIST, {
        ecommerce: {
          item_list_id: id,
          item_list_name: name,
          items: items.map((event, index) => ({
            index: index + 1,
            item_id: event.id,
            item_name: event.name,
            item_brand: ANALYTICS_EVENTS.PROVIDERS.TICKETS,
            item_category: ANALYTICS_EVENTS.SELECT_ITEM.CATEGORY.TICKETS,
            item_variant: EVENT_VARIANTS[variant as EventVariantKeys],
          })),
        },
      })
    },
    [clearEcommerceEvent, sendAnalytics],
  )

  const sendSelectedItemEvent = useCallback(
    ({ id, name, item }: AnalyticsSelectItemEventProps) => {
      clearEcommerceEvent()
      sendAnalytics(ANALYTICS_EVENTS.EVENT_TYPES.SELECT_ITEM, {
        ecommerce: {
          item_list_id: id,
          item_list_name: name,
          items: [
            {
              item_id: item.id,
              item_name: item.name,
              item_brand: ANALYTICS_EVENTS.PROVIDERS.TICKETS,
              item_category: ANALYTICS_EVENTS.SELECT_ITEM.CATEGORY.TICKETS,
              item_variant: EVENT_VARIANTS[item.variant as EventVariantKeys],
            },
          ],
        },
      })
    },
    [clearEcommerceEvent, sendAnalytics],
  )

  const sendAddToCartEvent = useCallback(
    ({ id, price, name, category, variant, brand, quantity }: AnalyticsAddToCartEventProps) => {
      clearEcommerceEvent()
      sendAnalytics(ANALYTICS_EVENTS.EVENT_TYPES.ADD_TO_CART, {
        booking_flow_variant: bookingVariant,
        ecommerce: {
          currency: currentPackage?.cartInfo?.currency,
          value: price,
          items: [
            {
              item_id: id,
              item_name: name,
              item_category: category,
              item_variant: variant,
              item_brand: brand,
              price,
              quantity,
            },
          ],
        },
      })
    },
    [bookingVariant, clearEcommerceEvent, currentPackage?.cartInfo?.currency, sendAnalytics],
  )

  const sendRemoveFromCartEvent = useCallback(
    (include: CartType) => {
      const { price, items } = getCartProducts(currentPackage, [include])

      clearEcommerceEvent()
      sendAnalytics(ANALYTICS_EVENTS.EVENT_TYPES.REMOVE_FROM_CART, {
        ecommerce: { currency: currentPackage?.cartInfo?.currency, value: price, items },
      })
    },
    [clearEcommerceEvent, currentPackage, sendAnalytics],
  )

  const sendFullCartEvent = useCallback(
    (eventType: string) => {
      const { price, items } = getCartProducts(currentPackage, [
        CART_TYPE.TICKET,
        CART_TYPE.HOTEL,
        CART_TYPE.FLIGHT,
        CART_TYPE.ADDONS,
      ])
      const shouldSendVariant = eventType === ANALYTICS_EVENTS.EVENT_TYPES.BEGIN_CHECKOUT

      clearEcommerceEvent()

      sendAnalytics(eventType, {
        ...(shouldSendVariant && {
          booking_flow_variant: bookingVariant,
        }),
        ecommerce: {
          currency: currentPackage?.cartInfo?.currency,
          value: price,
          items,
        },
      })
    },
    [bookingVariant, clearEcommerceEvent, currentPackage, sendAnalytics],
  )

  const sendFunnelCartEvent = useCallback(
    (eventType: string) => {
      const { price } = getCartProducts(currentPackage, [
        CART_TYPE.TICKET,
        CART_TYPE.HOTEL,
        CART_TYPE.FLIGHT,
        CART_TYPE.ADDONS,
      ])
      const shouldSendVariant = eventType === ANALYTICS_EVENTS.EVENT_TYPES.REACHED_REVIEW

      clearEcommerceEvent()
      sendAnalytics(eventType, {
        ...(shouldSendVariant && {
          booking_flow_variant: bookingVariant,
        }),
        ecommerce: {
          currency: currentPackage?.cartInfo?.currency,
          funnel_value: price,
        },
      })
    },
    [bookingVariant, clearEcommerceEvent, currentPackage, sendAnalytics],
  )

  const sendBeginCheckoutEvent = useCallback(
    () => sendFullCartEvent(ANALYTICS_EVENTS.EVENT_TYPES.BEGIN_CHECKOUT),
    [sendFullCartEvent],
  )

  const sendReachedReviewEvent = useCallback(
    () => sendFunnelCartEvent(ANALYTICS_EVENTS.EVENT_TYPES.REACHED_REVIEW),
    [sendFunnelCartEvent],
  )

  const sendReachedEmailCheckoutEvent = useCallback(
    () => sendFunnelCartEvent(ANALYTICS_EVENTS.EVENT_TYPES.REACHED_EMAIL_CHECKOUT),
    [sendFunnelCartEvent],
  )

  const sendContactInfoSubmitEvent = useCallback(
    () => sendFullCartEvent(ANALYTICS_EVENTS.EVENT_TYPES.CONTACT_INFO_SUBMIT),
    [sendFullCartEvent],
  )

  const sendPayNowEvent = ({ coupon, paymentType }: AnalyticsPayNowProps) => {
    const { price, items } = getCartProducts(currentPackage, [
      CART_TYPE.TICKET,
      CART_TYPE.HOTEL,
      CART_TYPE.FLIGHT,
      CART_TYPE.ADDONS,
    ])

    clearEcommerceEvent()
    sendAnalytics(ANALYTICS_EVENTS.EVENT_TYPES.ADD_PAYMENT_INFO, {
      ecommerce: {
        currency: currentPackage?.cartInfo?.currency,
        value: price,
        items,
        coupon,
        payment_type: paymentType,
      },
    })
  }

  const sendUserExperienceEvent = () => {
    clearEcommerceEvent()
    sendAnalytics(ANALYTICS_EVENTS.EVENT_TYPES.EXPERIMENT_VIEWED, {
      booking_flow_variant: bookingVariant,
      ecommerce: {
        experiment_id: 'booking_review_page_v1',
      },
    })
  }

  const sendOrderConfirmationEvent = (coupon: string, bookingId: string) => {
    const { price, items } = getCartProducts(currentPackage, [
      CART_TYPE.TICKET,
      CART_TYPE.HOTEL,
      CART_TYPE.FLIGHT,
      CART_TYPE.ADDONS,
    ])

    clearEcommerceEvent()
    sendAnalytics(ANALYTICS_EVENTS.EVENT_TYPES.ORDER_PURCHASE, {
      booking_flow_variant: bookingVariant,
      ecommerce: {
        currency: currentPackage?.cartInfo?.currency,
        value: price,
        tax: currentPackage?.cartInfo.pricing.total.totalTaxes,
        items,
        coupon,
        transaction_id: bookingId,
      },
    })
  }

  return {
    sendAnalytics,
    sendNavigationEvent,
    sendFormSubmitEvent,
    sendViewItemListEvent,
    sendSelectedItemEvent,
    sendAddToCartEvent,
    sendRemoveFromCartEvent,
    sendBeginCheckoutEvent,
    sendReachedReviewEvent,
    sendReachedEmailCheckoutEvent,
    sendContactInfoSubmitEvent,
    clearEcommerceEvent,
    sendPayNowEvent,
    sendOrderConfirmationEvent,
    sendUserExperienceEvent,
  }
  // eslint-disable-next-line max-lines
}

export default useAnalytics
